import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { ConstantUtil } from '@/utils/ConstantUtil'
import { deviceService } from '@/utils/deviceService'
import { LogService } from '@/utils/logService'
import { IRefreshTokenResponse } from '@/types/auth'

// ===============================
// 🔧 基礎設定
// ===============================
const baseQuery = fetchBaseQuery({
  baseUrl: ConstantUtil.API_BASE_URL,
  prepareHeaders: (headers) => {
    // 1️⃣ 加入 satoken
    const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN)
    if (token) {
      headers.set('satoken', token)
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]))
        LogService.info('Product API Token 使用追蹤', {
          userId: tokenPayload.sub || tokenPayload.id,
          userType: tokenPayload.type || 'unknown',
          issuedAt: new Date(tokenPayload.iat * 1000).toISOString(),
          expiresAt: new Date(tokenPayload.exp * 1000).toISOString(),
        })
      } catch (error) {
        LogService.warn('無法解析 JWT token', { error: (error as Error).message })
      }
    }

    // 2️⃣ 加入裝置資訊
    const deviceData = deviceService.getDeviceInfo()
    headers.set('deviceData', encodeURIComponent(JSON.stringify(deviceData)))

    // 3️⃣ 設定內容類型
    headers.set('Content-Type', 'application/json')
    return headers
  },
  timeout: ConstantUtil.API_TIMEOUT,
})

// ===============================
// 🔒 Token Refresh 包裝
// ===============================
const baseQueryWithTokenRefresh = async (args: any, api: any, extraOptions: any) => {
  let result = await baseQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    console.warn('⚠️ Access Token 過期，開始嘗試 Refresh Token')

    const refreshToken = localStorage.getItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN)
    if (!refreshToken) return result

    const rawBaseQuery = fetchBaseQuery({
      baseUrl: ConstantUtil.API_BASE_URL,
      prepareHeaders: (headers) => {
        headers.set('Content-Type', 'application/json')
        return headers
      },
    })

    const refreshResult = await rawBaseQuery(
      {
        url: ConstantUtil.API_REFRESH_TOKEN,
        method: 'POST',
        body: { refreshToken },
      },
      api,
      extraOptions
    )

    const refreshBody = refreshResult.data as IRefreshTokenResponse | undefined
    const refreshData = refreshBody?.data

    if (refreshData?.accessToken) {
      const { accessToken, refreshToken: newRefreshToken } = refreshData
      localStorage.setItem(ConstantUtil.STORAGE_USER_TOKEN, accessToken)
      if (newRefreshToken) {
        localStorage.setItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN, newRefreshToken)
      }

      // 🔁 用新 Token 重試原請求
      return await baseQuery(args, api, extraOptions)
    }

    // ❌ Refresh 失敗 → 導回登入頁
    alert('您的登入已逾時，請重新登入')
    localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN)
    localStorage.removeItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN)
    localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO)
    window.location.href = '/login'
  }

  return result
}

// ===============================
// 🛍️ 商品 API
// ===============================
export const productApi = createApi({
  reducerPath: 'productApi',
  baseQuery: baseQueryWithTokenRefresh,
  tagTypes: ['Product'],
  endpoints: (builder) => ({
    // ✅ 取得商品列表
    getProducts: builder.query({
      query: () => ConstantUtil.API_PRODUCTS,
      providesTags: ['Product'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_PRODUCTS,
            'GET',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Get Products API call failed', error)
        }
      },
    }),

    // ✅ 取得商品詳情
    getProductById: builder.query({
      query: (id: string) => `${ConstantUtil.API_PRODUCTS}/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Product', id }],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_PRODUCTS,
            'GET',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Get Product Detail API call failed', error)
        }
      },
    }),
  }),
})

export const { 
  useGetProductsQuery,
  useGetProductByIdQuery
} = productApi
